import React, { useState } from "react";
import { RotateCcw } from "lucide-react";
import { adminRequest } from "../api";
import { useAdminContext } from "../context";
import { InlineNotice, Section, ShortId, TypeConfirmDialog } from "./ui";

type Preview = { wallet: string; xUserId: string; username: string; wallets: string[]; reviewHash: string; confirmation: string };
export function SignupReset({ wallet, onDeleted }: { wallet: string; onDeleted: () => void }) {
  const { tokens, identity } = useAdminContext();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [confirming, setConfirming] = useState(false), [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null), [deleted, setDeleted] = useState(false);
  const [pending, setPending] = useState<{ id: string; body: string } | null>(null);
  if (!identity.capabilities.includes("admin:support")) return null;
  async function review() {
    setBusy(true); setNotice(null);
    try {
      const response = await adminRequest<Preview>(`/admin/players/${encodeURIComponent(wallet)}/signup-reset`, tokens);
      setPreview(response.data); setPending(null);
    } catch (cause) { setNotice(cause instanceof Error ? cause.message : "Account review unavailable."); }
    finally { setBusy(false); }
  }
  async function apply(reason: string, confirmation: string) {
    if (!preview) return;
    const request = pending ?? { id: crypto.randomUUID(), body: JSON.stringify({ wallet, xUserId: preview.xUserId,
      username: preview.username, reviewHash: preview.reviewHash, reason, confirmation }) };
    setPending(request); setBusy(true);
    try {
      const response = await adminRequest<{ audit?: { status: string }; result?: { deleted: boolean } }>("/admin/support/delete-signup", tokens,
        { method: "POST", headers: { "x-idempotency-key": request.id }, body: request.body });
      if (response.data.audit?.status !== "SUCCEEDED" || !response.data.result?.deleted) throw Error("Deletion is not confirmed yet. Retry the same request to check it.");
      setDeleted(true); setPending(null); setPreview(null);
      setNotice("Test account deleted. Disconnect the old wallet, connect a fresh wallet, then sign in with the same X account. The intro and game progress start from zero.");
      onDeleted();
    } catch (cause) { setNotice(cause instanceof Error ? cause.message : "Deletion was not confirmed. Retry the same request."); }
    finally { setBusy(false); setConfirming(false); }
  }
  return <Section title="Start a fresh X signup" description="Stage only · same X account, fresh wallet">
    <p>Delete this X signup and detach every linked wallet. Its old wallets will no longer be able to log in. The next signup starts with no game progress, inventory, equipment, runs, or claims.</p>
    <p className="muted">Use a fresh wallet next time. This does not erase past blockchain transactions or settlement records.</p>
    {notice ? <InlineNotice tone={deleted ? "success" : "warning"}>{notice}</InlineNotice> : null}
    {!deleted && !preview ? <button className="button secondary" disabled={busy} onClick={() => void review()}><RotateCcw />{busy ? "Reading account…" : "Review account deletion"}</button> : null}
    {preview ? <div aria-label="Account deletion review">
      <p><strong>@{preview.username}</strong> · {preview.wallets.length} linked {preview.wallets.length === 1 ? "wallet" : "wallets"}</p>
      <ul>{preview.wallets.map(address => <li key={address}><ShortId value={address} /></li>)}</ul>
      <div className="type-confirm-actions">
        <button className="button secondary" disabled={busy} onClick={() => { setPreview(null); setPending(null); }}>Cancel</button>
        <button className="button danger" disabled={busy} onClick={() => pending ? void apply("", "") : setConfirming(true)}>{busy ? "Deleting…" : pending ? "Retry same deletion" : "Delete test account"}</button>
      </div>
    </div> : null}
    <TypeConfirmDialog open={confirming} title={`Delete @${preview?.username ?? ""} from Stage?`}
      detail="All listed wallets will be detached and their sessions disabled. Sign up again with the same X account and a fresh wallet. This cannot be undone from this console."
      phrase={preview?.confirmation ?? ""} busy={busy} onCancel={() => setConfirming(false)} onConfirm={(reason, confirmation) => void apply(reason, confirmation)} submitLabel="Delete test account" />
  </Section>;
}
